import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateManufacturerFreightRule,
  useDeleteManufacturerFreightRule,
  useManufacturerFreightProductGroups,
  useUpdateManufacturerFreightRule,
} from "@/api/hooks";
import type { components } from "@/api/schema";
import {
  useCreateSourceFreightRule,
  useDeleteSourceFreightRule,
  useUpdateSourceFreightRule,
} from "@/api/sourcing";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  InlineFieldInput,
  InlineFieldTextarea,
} from "@/components/inline-field-group";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ActionPopover } from "@/components/action-popover";
import { LoadingButton } from "@/components/loading-button";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { formatMoney, formatPercent } from "@/lib/utils";
import { DeliveryFees } from "./delivery-fees";
import { SearchInput } from "@/components/search-input";

type FreightRuleBody = Record<string, unknown> & { rule_type: string };
type MutateOptions = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

interface FreightRuleActions {
  create: {
    mutate: (body: FreightRuleBody, options: MutateOptions) => void;
    isPending: boolean;
  };
  update: {
    mutate: (
      vars: { ruleId: number; body: FreightRuleBody },
      options: MutateOptions,
    ) => void;
    isPending: boolean;
  };
  remove: {
    mutate: (ruleId: number, options: MutateOptions) => void;
    isPending: boolean;
    variables?: number;
  };
}

type FreightRuleConditions = components["schemas"]["FreightRuleConditionsRead"];
type FreightProductGroups = components["schemas"]["FreightProductGroupsRead"];
type ProductScope = "all" | "only" | "except";

const PRODUCT_SCOPE_OPTIONS = [
  { value: "all", label: "All products" },
  { value: "only", label: "Only these" },
  { value: "except", label: "All except" },
] as const;

export interface FreightRuleData {
  id: number;
  rule_type: string;
  ff_threshold_net?: string | null;
  below_threshold_percent?: string | null;
  origin_zip_codes?: string | null;
  priority: number;
  effective_from?: string | null;
  effective_to?: string | null;
  notes?: string | null;
  enabled: boolean;
  conditions?: FreightRuleConditions | null;
}

function freightOriginZipDisplay(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(String).join(", ");
  } catch {
    // Legacy rows can contain comma-separated values instead of JSON.
  }
  return raw;
}

const FREIGHT_RULE_TYPE_OPTIONS: {
  value: string;
  label: string;
  description: string;
}[] = [
  {
    value: "always_free",
    label: "Always Free",
    description: "Manufacturer always covers freight",
  },
  {
    value: "threshold",
    label: "Threshold",
    description:
      "Free freight at or above a NET threshold; we book freight below it",
  },
  {
    value: "threshold_with_percent",
    label: "Threshold + %",
    description:
      "Free freight at or above the threshold; manufacturer ships at a % surcharge below",
  },
  {
    value: "percent_only",
    label: "Percent Only",
    description:
      "Manufacturer always ships, charging a fixed percentage of order",
  },
];

function freightRuleTypeLabel(type: string): string {
  return FREIGHT_RULE_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

function piecesLabel(count: number): string {
  return `${count} ${count === 1 ? "piece" : "pieces"}`;
}

function productScope(conditions?: FreightRuleConditions | null): ProductScope {
  if (conditions?.exclude_category_ids.length) return "except";
  if (conditions?.discount_ids.length || conditions?.category_ids.length)
    return "only";
  return "all";
}

function groupNames(
  ids: number[],
  options: { id: number; name: string }[] | undefined,
): string[] {
  return ids.map(
    (id) => options?.find((option) => option.id === id)?.name ?? `#${id}`,
  );
}

/** Each AQ parent category with its child categories and their product counts. */
function categoryTree(groups: FreightProductGroups | undefined) {
  const parents = new Map<
    number | null,
    {
      id: number | null;
      name: string;
      products: number;
      children: FreightProductGroups["categories"];
    }
  >();
  for (const category of groups?.categories ?? []) {
    const parentId = category.parent_id ?? null;
    const parent = parents.get(parentId) ?? {
      id: parentId,
      name: category.parent_name ?? "Other",
      products: 0,
      children: [],
    };
    parent.products += category.products;
    parent.children.push(category);
    parents.set(parentId, parent);
  }
  return [...parents.values()];
}

function scopeSummary(
  conditions: FreightRuleConditions,
  groups: FreightProductGroups | undefined,
): string[] {
  const parts: string[] = [];
  const categoryOptions = [
    ...(groups?.categories ?? []),
    ...(groups?.categories ?? []).flatMap((category) =>
      category.parent_id != null
        ? [{ id: category.parent_id, name: category.parent_name ?? "" }]
        : [],
    ),
  ];
  const included = [
    ...groupNames(conditions.discount_ids, groups?.discount_categories),
    ...groupNames(conditions.category_ids, categoryOptions),
  ];
  if (included.length) parts.push(`Only ${included.join(", ")}`);
  if (conditions.exclude_category_ids.length)
    parts.push(
      `Except ${groupNames(conditions.exclude_category_ids, categoryOptions).join(", ")}`,
    );
  if (conditions.min_order_net != null)
    parts.push(`From ${formatMoney(conditions.min_order_net)} Net`);
  if (conditions.destination_states.length)
    parts.push(`Ship to ${conditions.destination_states.join(", ")}`);
  if (conditions.direct_orders_only) parts.push("Direct orders only");
  return parts;
}

function FreightProductPicker({
  groups,
  loading,
  failed,
  onRetry,
  scope,
  discountIds,
  categoryIds,
  onChange,
}: {
  groups: FreightProductGroups | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  scope: Exclude<ProductScope, "all">;
  discountIds: number[];
  categoryIds: number[];
  onChange: (next: { discountIds: number[]; categoryIds: number[] }) => void;
}) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const matches = (name: string) => name.toLowerCase().includes(query);
  const lines =
    scope === "only" && (groups?.discount_categories.length ?? 0) > 1
      ? (groups?.discount_categories ?? []).filter((line) => matches(line.name))
      : [];
  // Ordered by the choice the dialog opened with, so rows do not jump while checking.
  const [openedWith] = useState(categoryIds);
  const isChosen = (parent: ReturnType<typeof categoryTree>[number]) =>
    (parent.id != null && openedWith.includes(parent.id)) ||
    parent.children.some((child) => openedWith.includes(child.id));
  const tree = categoryTree(groups)
    .map((parent) => ({
      ...parent,
      children: matches(parent.name)
        ? parent.children
        : parent.children.filter((child) => matches(child.name)),
    }))
    .filter((parent) => parent.children.length > 0)
    // Chosen groups first, so the current choice is visible without scrolling.
    .sort((left, right) => Number(isChosen(right)) - Number(isChosen(left)));
  const toggle = (list: number[], id: number, checked: boolean) =>
    checked ? [...list, id] : list.filter((value) => value !== id);
  const row =
    "flex min-h-11 cursor-pointer items-center gap-3 rounded px-3 py-2 text-sm hover:bg-accent";
  const count = "ml-auto font-mono text-xs tabular-nums text-muted-foreground";

  return (
    <div className="space-y-2">
      <SearchInput
        aria-label="Search products"
        placeholder="Search product lines and categories"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="max-h-64 overflow-y-auto rounded-md border p-1">
        {loading ? (
          <p className="p-3 text-sm text-muted-foreground">Loading…</p>
        ) : failed ? (
          <div className="p-3 text-sm" role="alert">
            Could not load product groups.{" "}
            <Button type="button" variant="link" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : lines.length === 0 && tree.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No matches</p>
        ) : (
          <>
            {lines.length > 0 && (
              <div role="group" aria-label="Product lines">
                <p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
                  Product lines
                </p>
                {lines.map((line) => (
                  <label key={`line-${line.id}`} className={row}>
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      checked={discountIds.includes(line.id)}
                      onChange={(event) =>
                        onChange({
                          discountIds: toggle(
                            discountIds,
                            line.id,
                            event.target.checked,
                          ),
                          categoryIds,
                        })
                      }
                    />
                    <span>{line.name}</span>
                    <span className={count}>{line.products}</span>
                  </label>
                ))}
              </div>
            )}
            {tree.length > 0 && (
              <div role="group" aria-label="Categories">
                {lines.length > 0 && (
                  <p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
                    Categories
                  </p>
                )}
                {tree.map((parent) => {
                  const parentChecked =
                    parent.id != null && categoryIds.includes(parent.id);
                  return (
                    <div key={`parent-${parent.id ?? "none"}`}>
                      {parent.id != null ? (
                        <label className={`${row} font-medium`}>
                          <input
                            type="checkbox"
                            className="size-4 shrink-0 accent-primary"
                            checked={parentChecked}
                            onChange={(event) =>
                              onChange({
                                discountIds,
                                categoryIds: toggle(
                                  categoryIds,
                                  parent.id as number,
                                  event.target.checked,
                                ),
                              })
                            }
                          />
                          <span>{parent.name}</span>
                          <span className={count}>{parent.products}</span>
                        </label>
                      ) : (
                        <p className="px-3 pt-2 pb-1 text-sm font-medium">
                          {parent.name}
                        </p>
                      )}
                      {parent.children.map((child) => (
                        <label
                          key={`category-${child.id}`}
                          className={`${row} pl-9`}
                        >
                          <input
                            type="checkbox"
                            className="size-4 shrink-0 accent-primary"
                            checked={
                              parentChecked || categoryIds.includes(child.id)
                            }
                            disabled={parentChecked}
                            onChange={(event) =>
                              onChange({
                                discountIds,
                                categoryIds: toggle(
                                  categoryIds,
                                  child.id,
                                  event.target.checked,
                                ),
                              })
                            }
                          />
                          <span>{child.name}</span>
                          <span className={count}>{child.products}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** A number as typed, formatted for reading when it parses; otherwise as typed. */
function readableNumber(raw: string, format: (value: number) => string) {
  if (raw.trim() === "") return "";
  const value = Number(raw.replace(/,/g, ""));
  return Number.isFinite(value) ? format(value) : raw;
}

/**
 * One of a rule's values. A saved rule's values are click-to-edit; a new rule's are boxes
 * (USA-1580). Either way the dialog's Save sends the whole rule.
 */
function RuleField({
  saved,
  id,
  label,
  value,
  onChange,
  display,
  empty = "Not set",
  placeholder,
  inputMode,
  type,
  invalid = false,
  multiline = false,
  prefix,
  suffix,
}: {
  saved: boolean;
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The read view of a saved rule's value, e.g. formatted money. */
  display?: string;
  /** The read view when the value is blank. */
  empty?: string;
  placeholder?: string;
  inputMode?: "decimal" | "numeric";
  type?: "number";
  invalid?: boolean;
  multiline?: boolean;
  prefix?: string;
  suffix?: string;
}) {
  if (saved) {
    return (
      <POInlineEditableField
        value={value}
        displayValue={display ?? value}
        placeholder={empty}
        kind={multiline ? "textarea" : "text"}
        ariaLabel={label}
        invalid={invalid}
        showEditIcon
        allowWrap={multiline}
        buttonClassName="-mx-2"
        onConfirm={onChange}
      />
    );
  }
  if (multiline) {
    return (
      <InlineFieldTextarea
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
      />
    );
  }
  return (
    <div className="relative">
      {prefix && (
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {prefix}
        </span>
      )}
      <InlineFieldInput
        id={id}
        type={type}
        step={type === "number" ? "1" : undefined}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={prefix ? "pl-6" : suffix ? "pr-7" : undefined}
        aria-invalid={invalid}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}

function FreightRuleDialog({
  actions,
  manufacturerUuid,
  rule,
  open,
  onOpenChange,
}: {
  actions: FreightRuleActions;
  manufacturerUuid: string;
  rule: FreightRuleData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = rule !== null;
  const createRule = actions.create;
  const updateRule = actions.update;
  const mutation = isEdit ? updateRule : createRule;

  const [ruleType, setRuleType] = useState<string>(
    rule?.rule_type ?? "threshold",
  );
  const [threshold, setThreshold] = useState<string>(
    rule?.ff_threshold_net ?? "",
  );
  const [percent, setPercent] = useState<string>(
    rule?.below_threshold_percent != null
      ? String(Number(rule.below_threshold_percent) * 100)
      : "",
  );
  const [notes, setNotes] = useState<string>(rule?.notes ?? "");
  const [originZips, setOriginZips] = useState<string>(
    freightOriginZipDisplay(rule?.origin_zip_codes),
  );
  const [priority, setPriority] = useState<string>(String(rule?.priority ?? 0));
  const [effectiveFrom, setEffectiveFrom] = useState<string>(
    rule?.effective_from ?? "",
  );
  const [effectiveTo, setEffectiveTo] = useState<string>(
    rule?.effective_to ?? "",
  );
  const [enabled, setEnabled] = useState<boolean>(rule?.enabled ?? true);
  const conditions = rule?.conditions;
  const [minUnits, setMinUnits] = useState<string>(
    conditions?.ff_min_units != null ? String(conditions.ff_min_units) : "",
  );
  const [minOrder, setMinOrder] = useState<string>(
    conditions?.min_order_net != null ? String(conditions.min_order_net) : "",
  );
  const [scope, setScope] = useState<ProductScope>(productScope(conditions));
  const [discountIds, setDiscountIds] = useState<number[]>(
    conditions?.discount_ids ?? [],
  );
  const [categoryIds, setCategoryIds] = useState<number[]>(
    productScope(conditions) === "except"
      ? (conditions?.exclude_category_ids ?? [])
      : (conditions?.category_ids ?? []),
  );
  const [states, setStates] = useState<string>(
    (conditions?.destination_states ?? []).join(", "),
  );
  const [directOnly, setDirectOnly] = useState<boolean>(
    conditions?.direct_orders_only ?? false,
  );
  const [lastRuleId, setLastRuleId] = useState<number | null>(rule?.id ?? null);
  const productGroups = useManufacturerFreightProductGroups(
    manufacturerUuid,
    open && scope !== "all",
  );

  // Reset form when the dialog target changes (different rule, or open/close).
  // Mirrors the upstream-tracking pattern used in ManufacturerSettingsPanel.
  if ((rule?.id ?? null) !== lastRuleId) {
    setLastRuleId(rule?.id ?? null);
    setRuleType(rule?.rule_type ?? "threshold");
    setThreshold(rule?.ff_threshold_net ?? "");
    setPercent(
      rule?.below_threshold_percent != null
        ? String(Number(rule.below_threshold_percent) * 100)
        : "",
    );
    setNotes(rule?.notes ?? "");
    setOriginZips(freightOriginZipDisplay(rule?.origin_zip_codes));
    setPriority(String(rule?.priority ?? 0));
    setEffectiveFrom(rule?.effective_from ?? "");
    setEffectiveTo(rule?.effective_to ?? "");
    setEnabled(rule?.enabled ?? true);
    const next = rule?.conditions;
    setMinUnits(next?.ff_min_units != null ? String(next.ff_min_units) : "");
    setMinOrder(next?.min_order_net != null ? String(next.min_order_net) : "");
    setScope(productScope(next));
    setDiscountIds(next?.discount_ids ?? []);
    setCategoryIds(
      productScope(next) === "except"
        ? (next?.exclude_category_ids ?? [])
        : (next?.category_ids ?? []),
    );
    setStates((next?.destination_states ?? []).join(", "));
    setDirectOnly(next?.direct_orders_only ?? false);
  }

  const needsThreshold =
    ruleType === "threshold" || ruleType === "threshold_with_percent";
  const needsPercent =
    ruleType === "threshold_with_percent" || ruleType === "percent_only";

  const parsedThreshold =
    threshold.trim() === "" ? null : Number(threshold.replace(/,/g, ""));
  const parsedPercent = percent.trim() === "" ? null : Number(percent);
  const parsedMinUnits = minUnits.trim() === "" ? null : Number(minUnits);
  const minUnitsValid =
    parsedMinUnits === null ||
    (Number.isInteger(parsedMinUnits) && parsedMinUnits >= 1);
  const parsedMinOrder =
    minOrder.trim() === "" ? null : Number(minOrder.replace(/,/g, ""));
  const minOrderValid =
    parsedMinOrder === null ||
    (Number.isFinite(parsedMinOrder) && parsedMinOrder >= 0);
  const parsedStates = states
    .split(/[,\s]+/)
    .map((state) => state.trim().toUpperCase())
    .filter(Boolean);
  const statesValid = parsedStates.every((state) => /^[A-Z]{2}$/.test(state));
  const productsValid =
    scope === "all" || discountIds.length + categoryIds.length > 0;
  const thresholdValid =
    !needsThreshold ||
    (parsedThreshold !== null &&
      Number.isFinite(parsedThreshold) &&
      parsedThreshold >= 0) ||
    (parsedThreshold === null && parsedMinUnits !== null);
  const percentValid =
    !needsPercent ||
    (parsedPercent !== null &&
      Number.isFinite(parsedPercent) &&
      parsedPercent >= 0 &&
      parsedPercent <= 100);
  const parsedPriority = Number(priority);
  const priorityValid = Number.isInteger(parsedPriority);
  const effectiveDatesValid =
    !effectiveFrom || !effectiveTo || effectiveFrom <= effectiveTo;
  const formValid =
    thresholdValid &&
    percentValid &&
    priorityValid &&
    effectiveDatesValid &&
    minUnitsValid &&
    minOrderValid &&
    statesValid &&
    productsValid;

  const handleSave = () => {
    if (!formValid) return;
    const body = {
      rule_type: ruleType,
      ff_threshold_net:
        needsThreshold && parsedThreshold !== null
          ? String(parsedThreshold)
          : null,
      below_threshold_percent:
        needsPercent && parsedPercent !== null
          ? String(parsedPercent / 100)
          : null,
      origin_zip_codes:
        originZips.trim() === ""
          ? null
          : JSON.stringify(
              originZips
                .split(/[;,\s]+/)
                .map((zip) => zip.trim())
                .filter(Boolean),
            ),
      priority: parsedPriority,
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      notes: notes.trim() === "" ? null : notes.trim(),
      enabled,
      conditions: {
        min_order_net: parsedMinOrder !== null ? String(parsedMinOrder) : null,
        ff_min_units: needsThreshold ? parsedMinUnits : null,
        discount_ids: scope === "only" ? discountIds : [],
        category_ids: scope === "only" ? categoryIds : [],
        exclude_category_ids: scope === "except" ? categoryIds : [],
        destination_states: parsedStates,
        direct_orders_only: directOnly,
      },
    };

    const onSuccess = () => {
      toast.success(isEdit ? "Freight rule updated" : "Freight rule added");
      onOpenChange(false);
    };
    const onError = (err: unknown) => {
      const detail =
        err && typeof err === "object" && "detail" in err
          ? String((err as { detail: unknown }).detail)
          : "Failed to save freight rule";
      toast.error(detail);
    };

    if (isEdit && rule) {
      updateRule.mutate({ ruleId: rule.id, body }, { onSuccess, onError });
    } else {
      createRule.mutate(body, { onSuccess, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Freight Rule" : "Add Freight Rule"}
          </DialogTitle>
          <DialogDescription>
            Free freight thresholds and freight charges for this source.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="freight-rule-type">Rule Type</Label>
            <Select value={ruleType} onValueChange={setRuleType}>
              <SelectTrigger id="freight-rule-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREIGHT_RULE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {
                FREIGHT_RULE_TYPE_OPTIONS.find((o) => o.value === ruleType)
                  ?.description
              }
            </p>
          </div>

          {needsThreshold && (
            <div className="grid grid-cols-[1fr_8rem] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="freight-threshold">
                  Free Freight Threshold (Net)
                </Label>
                <RuleField
                  saved={isEdit}
                  id="freight-threshold"
                  label="Free Freight Threshold (Net)"
                  prefix="$"
                  inputMode="decimal"
                  placeholder="8000"
                  value={threshold}
                  onChange={setThreshold}
                  display={readableNumber(threshold, formatMoney)}
                  invalid={!thresholdValid}
                />
                {!thresholdValid && (
                  <p className="text-xs text-destructive">
                    Enter a dollar amount or a piece count.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="freight-min-units">Or pieces</Label>
                <RuleField
                  saved={isEdit}
                  id="freight-min-units"
                  label="Or pieces"
                  inputMode="numeric"
                  placeholder="3"
                  value={minUnits}
                  onChange={setMinUnits}
                  display={readableNumber(minUnits, piecesLabel)}
                  invalid={!minUnitsValid}
                />
                {!minUnitsValid && (
                  <p className="text-xs text-destructive">
                    Enter a whole number.
                  </p>
                )}
              </div>
            </div>
          )}

          {needsPercent && (
            <div className="space-y-1.5">
              <Label htmlFor="freight-percent">Below-Threshold Freight %</Label>
              <RuleField
                saved={isEdit}
                id="freight-percent"
                label="Below-Threshold Freight %"
                suffix="%"
                inputMode="decimal"
                placeholder="3"
                value={percent}
                onChange={setPercent}
                display={readableNumber(percent, formatPercent)}
                invalid={!percentValid}
              />
              {!percentValid && (
                <p className="text-xs text-destructive">
                  Enter a percentage between 0 and 100.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <p id="freight-products-label" className="text-sm font-medium">
              Products
            </p>
            <ReviewChoiceGroup
              label="Products"
              value={scope}
              options={PRODUCT_SCOPE_OPTIONS}
              onChange={(next) => {
                setScope(next);
                setDiscountIds([]);
                setCategoryIds([]);
              }}
            />
            {scope !== "all" && (
              <FreightProductPicker
                groups={productGroups.data}
                loading={productGroups.isPending}
                failed={productGroups.isError}
                onRetry={() => productGroups.refetch()}
                scope={scope}
                discountIds={discountIds}
                categoryIds={categoryIds}
                onChange={(next) => {
                  setDiscountIds(next.discountIds);
                  setCategoryIds(next.categoryIds);
                }}
              />
            )}
            {!productsValid && (
              <p className="text-xs text-destructive">
                Choose at least one product line or category.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="freight-min-order">Applies from (Net)</Label>
              <RuleField
                saved={isEdit}
                id="freight-min-order"
                label="Applies from (Net)"
                prefix="$"
                inputMode="decimal"
                placeholder="0"
                value={minOrder}
                onChange={setMinOrder}
                display={readableNumber(minOrder, formatMoney)}
                empty="Any order"
                invalid={!minOrderValid}
              />
              {!minOrderValid && (
                <p className="text-xs text-destructive">
                  Enter a non-negative dollar amount.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freight-states">Ship-to states</Label>
              <RuleField
                saved={isEdit}
                id="freight-states"
                label="Ship-to states"
                placeholder="All states"
                value={states}
                onChange={setStates}
                empty="All states"
                invalid={!statesValid}
              />
              {!statesValid && (
                <p className="text-xs text-destructive">
                  Use two-letter state codes, like TX.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="freight-origin-zips">Origin ZIP scope</Label>
            <RuleField
              saved={isEdit}
              id="freight-origin-zips"
              label="Origin ZIP scope"
              placeholder="e.g. 76051, 30301 (blank means all origins)"
              value={originZips}
              onChange={setOriginZips}
              empty="All origins"
            />
            <p className="text-xs text-muted-foreground">
              A matching origin-specific rule outranks a manufacturer-wide rule.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="freight-priority">Priority</Label>
              <RuleField
                saved={isEdit}
                id="freight-priority"
                label="Priority"
                type="number"
                value={priority}
                onChange={setPriority}
                invalid={!priorityValid}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freight-effective-from">Effective from</Label>
              <Input
                id="freight-effective-from"
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="freight-effective-to">Effective to</Label>
              <Input
                id="freight-effective-to"
                type="date"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
                aria-invalid={!effectiveDatesValid}
              />
            </div>
          </div>
          {!effectiveDatesValid && (
            <p className="text-xs text-destructive">
              Effective-to must be on or after effective-from.
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="freight-notes">Notes</Label>
            <RuleField
              saved={isEdit}
              id="freight-notes"
              label="Notes"
              multiline
              placeholder="e.g. Free freight at $8000"
              value={notes}
              onChange={setNotes}
              empty="No notes"
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <Label
              htmlFor="freight-direct-only"
              className="text-sm font-medium"
            >
              Quotes and direct orders only
            </Label>
            <Switch
              id="freight-direct-only"
              checked={directOnly}
              onCheckedChange={setDirectOnly}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Active</Label>
              <p className="text-xs text-muted-foreground">
                Disable to keep the rule on file without applying it.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <LoadingButton
            loading={mutation.isPending}
            loadingText="Saving..."
            onClick={handleSave}
            disabled={!formValid}
          >
            Save
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A source's freight program. The preferred source's rules are the manufacturer's
 * live freight rules; a backup source keeps its own until it is made preferred.
 */
export function FreightProgram({
  manufacturerUuid,
  optionId,
  rules,
}: {
  manufacturerUuid: string;
  /** Set for a backup source; omitted for the preferred source (live rules). */
  optionId?: number;
  rules: FreightRuleData[];
}) {
  const liveCreate = useCreateManufacturerFreightRule(manufacturerUuid);
  const liveUpdate = useUpdateManufacturerFreightRule(manufacturerUuid);
  const liveRemove = useDeleteManufacturerFreightRule(manufacturerUuid);
  const sourceCreate = useCreateSourceFreightRule(
    manufacturerUuid,
    optionId ?? 0,
  );
  const sourceUpdate = useUpdateSourceFreightRule(
    manufacturerUuid,
    optionId ?? 0,
  );
  const sourceRemove = useDeleteSourceFreightRule(
    manufacturerUuid,
    optionId ?? 0,
  );
  const actions = (optionId == null
    ? { create: liveCreate, update: liveUpdate, remove: liveRemove }
    : {
        create: sourceCreate,
        update: sourceUpdate,
        remove: sourceRemove,
      }) as unknown as FreightRuleActions;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FreightRuleData | null>(null);
  const productGroups = useManufacturerFreightProductGroups(
    manufacturerUuid,
    rules.some((rule) => productScope(rule.conditions) !== "all"),
  );

  return (
    <section aria-labelledby={`freight-program-${optionId ?? "live"}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3
          id={`freight-program-${optionId ?? "live"}`}
          className="text-sm font-semibold"
        >
          Freight program
        </h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-3.5" />
          Add rule
        </Button>
      </div>
      {rules.length === 0 ? (
        <p className="rounded-lg border px-3 py-2.5 text-sm text-muted-foreground">
          No freight rules
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="flex items-start gap-3 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-x-2 font-medium">
                  {freightRuleTypeLabel(rule.rule_type)}
                  {rule.ff_threshold_net != null && (
                    <span>
                      at {formatMoney(rule.ff_threshold_net)} Net
                      {rule.conditions?.ff_min_units != null &&
                        ` or ${piecesLabel(rule.conditions.ff_min_units)}`}
                    </span>
                  )}
                  {rule.ff_threshold_net == null &&
                    rule.conditions?.ff_min_units != null && (
                      <span>
                        at {piecesLabel(rule.conditions.ff_min_units)}
                      </span>
                    )}
                  {rule.below_threshold_percent != null && (
                    <span className="font-normal text-muted-foreground">
                      {formatPercent(
                        Number(rule.below_threshold_percent) * 100,
                      )}{" "}
                      below
                    </span>
                  )}
                  {!rule.enabled && <Badge variant="secondary">Disabled</Badge>}
                </p>
                {rule.conditions && (
                  <p className="text-xs">
                    {scopeSummary(rule.conditions, productGroups.data).join(
                      " · ",
                    )}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {freightOriginZipDisplay(rule.origin_zip_codes) ||
                    "All origins"}{" "}
                  · {rule.effective_from || "Any date"} →{" "}
                  {rule.effective_to || "Open"}
                  {rule.notes ? ` · ${rule.notes}` : ""}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                aria-label="Edit freight rule"
                onClick={() => {
                  setEditing(rule);
                  setDialogOpen(true);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
              <ActionPopover
                align="end"
                trigger={
                  <LoadingButton
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 text-destructive"
                    aria-label="Delete freight rule"
                    loading={
                      actions.remove.isPending &&
                      actions.remove.variables === rule.id
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </LoadingButton>
                }
                title="Delete this freight rule?"
                confirmLabel="Delete"
                confirmVariant="destructive"
                onConfirm={(_note, close) => {
                  close();
                  actions.remove.mutate(rule.id, {
                    onSuccess: () => toast.success("Freight rule deleted"),
                    onError: () => toast.error("Failed to delete freight rule"),
                  });
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {optionId == null && <DeliveryFees manufacturerUuid={manufacturerUuid} />}
      <FreightRuleDialog
        actions={actions}
        manufacturerUuid={manufacturerUuid}
        rule={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </section>
  );
}
